import { useCallback, useEffect } from 'react';
import { UseQuerySaveQuery } from 'ducks/types/query-types';
import { getQuery, setQuery } from 'ducks/user/utils';
import { subtractFromDate } from 'utils/dates';
import { mergeSearchQuery, parseQueries } from 'utils/queries';

// TODO: cant figure out why the search is one state behind
// select the date dropdown then select the job tabs and the date reverts

interface useQuerySaveProps {
  page: string | null;
  search: string;
}

export const useQuerySave = ({ page, search }: useQuerySaveProps) => {
  const parsed_queries = parseQueries<UseQuerySaveQuery>(search);
  useEffect(() => {
    const { range_name, start_date, finish_date } = parsed_queries;
    if (page && Object.keys(parsed_queries).length !== 0) {
      const stored_queries = getQuery();
      setQuery({
        ...stored_queries,
        [page]: {
          range_name: range_name || null,
          start_date: start_date || null,
          finish_date: finish_date || null,
          filter: null,
          max_rows: parsed_queries.max_rows || 50,
          created_by: parsed_queries.created_by || null,
          include_archived: parsed_queries.include_archived || null,
          refresh_timeout: parsed_queries.refresh_timeout || null,
        },
      });
    }
  }, [page, parsed_queries]);

  const mergeQueries = useCallback(
    (update_queries = {}) => {
      // get local storage queries
      const stored_queries = getQuery();
      const stored_page_queries = page ? stored_queries[page] : {};

      // merge parsed queries => stored queries => update queries
      const merged_queries = { ...parsed_queries, ...stored_page_queries, ...update_queries };

      // sends back new query string
      return mergeSearchQuery(search, merged_queries);
    },
    [page, parsed_queries, search],
  );

  const getJobsQuery = useCallback(() => {
    // get local storage queries
    const stored_queries = getQuery();
    const stored_page_queries = stored_queries.jobs || {};

    // custom logic for job query
    const merged_queries = {
      ...stored_page_queries,
      range_name: 'Last 7 Days',
      start_date: subtractFromDate(new Date(), { decrement: 7, format: 'M/D/YYYY hh:mm A', utc: true }),
      finish_date: null,
    };

    // sends back new query string
    return mergeSearchQuery(search, merged_queries);
  }, [search]);

  const resetPageQueries = useCallback(() => {
    const reset_queries = {
      range_name: page === 'jobs' ? 'Last 7 Days' : null,
      start_date:
        page === 'jobs' ? subtractFromDate(new Date(), { decrement: 7, format: 'M/D/YYYY hh:mm A', utc: true }) : null,
      finish_date: null,
      max_rows: '50',
      created_by: null,
      include_archived: null,
      parameter_name: null,
      parameter_value: null,
      custom: false,
      refresh_timeout: null,
    };

    // sends back new query string
    return mergeSearchQuery(search, reset_queries);
  }, [page, search]);

  const getPageQueries = useCallback(
    (key: string) => {
      // get local storage queries
      const stored_queries = getQuery();
      const stored_page_queries = stored_queries[key] || {};

      // merge parsed queries => stored queries => update queries
      const merged_queries = {
        ...parsed_queries,
        ...stored_page_queries,
        range_name: null,
        start_date: null,
        finish_date: null,
        filter: null,
      };

      // sends back new query string
      return mergeSearchQuery(search, merged_queries);
    },
    [parsed_queries, search],
  );

  return { mergeQueries, getPageQueries, getJobsQuery, resetPageQueries };
};
